import { useState } from "react"
import { Text2GivePhoneNumber } from "../../types"
import { Button } from "@planningcenter/doxy-web"
import { css } from "@emotion/react"
import {
  PaymentMethodsProvider,
  usePaymentMethodsContext,
} from "../../hooks/usePaymentMethodsContext"
import { Link } from "react-router-dom"
import Text2GiveNotActive from "./Text2GiveNotActive"
import Form from "./Form"
import { AlertMessage } from "source/shared/components"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import DeleteConfirmationDialog from "source/giving/profile/components/DeleteConfirmationDialog"
import { useGivingOrganizationContext } from "../../hooks/useGivingOrganizationContext"
import { useMutation } from "@tanstack/react-query"
import { useGivingPersonContext } from "../../hooks/useGivingPersonContext"

export default function Connected() {
  return (
    <PaymentMethodsProvider>
      <ConnectedContent />
    </PaymentMethodsProvider>
  )
}

function ConnectedContent() {
  const { givingOrganization } = useGivingOrganizationContext()
  const { paymentMethods } = usePaymentMethodsContext()
  const { givingPerson, refreshGivingPerson } = useGivingPersonContext()
  const text2GivePhoneNumber: Text2GivePhoneNumber =
    givingPerson.text_to_give_phone_number!

  const [showDialog, setShowDialog] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasErrors, setHasErrors] = useState(false)

  const { mutate: text2GiveMutate } = useMutation({
    mutationFn: () =>
      sessionApiClient.del("/giving/v2/me/text_to_give_phone_number"),
    onSuccess: () => {
      refreshGivingPerson()
    },
    onError: () => setHasErrors(true),
    onSettled() {
      setIsSaving(false)
    },
  })

  const destroyText2GivePhoneNumber = async () => {
    setShowDialog(false)
    setIsSaving(true)
    setHasErrors(false)

    try {
      text2GiveMutate()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <div className="d-f fd-c g-2">
        <div
          className="d-f fd-c g-2 p-2"
          css={css`
            border: 1px solid var(--color-tint6);
            border-radius: 8px;
          `}
        >
          {hasErrors && (
            <AlertMessage type="error" icon="general#exclamation-triangle">
              Something went wrong. Please try again.
            </AlertMessage>
          )}
          {!givingOrganization.text2give_enabled ||
          !givingOrganization.text2give_phone_number ? (
            <Text2GiveNotActive />
          ) : (
            <>
              {givingOrganization.text2give_phone_number && (
                <div className="d-f fd-c g-2">
                  <p className="mb-1">
                    Text GIVE and the amount you want to give to{" "}
                    <strong>
                      {givingOrganization.formatted_text2give_phone_number}
                    </strong>
                    .{" "}
                    <Link to="/giving/text-to-give/help" className="fw-500">
                      Learn more
                    </Link>
                    , or{" "}
                    <Link
                      to={`sms:${givingOrganization.text2give_phone_number}`}
                    >
                      tap here to start a text to {givingOrganization.name}
                    </Link>
                    .
                  </p>
                  <p className="mb-1">
                    Example: send &quot;<strong>GIVE $25</strong>&quot; for a
                    $25 donation to {givingOrganization.name}&#39;s{" "}
                    {text2GivePhoneNumber.default_fund_name || "General"} fund.
                  </p>
                </div>
              )}

              <div className="action-drawer">
                {paymentMethods && paymentMethods.length > 0 ? (
                  <Form />
                ) : (
                  <div>
                    <p>
                      No payment method available for{" "}
                      {text2GivePhoneNumber.phone_number}.
                    </p>
                    {!givingOrganization.credit_cards_allowed && (
                      <p>
                        {givingOrganization.name} does not accept credit card
                        donations (prepaid gift cards and debit cards are
                        allowed).
                      </p>
                    )}
                    <Link
                      to="/me/profile/payment-methods"
                      className="btn minor-btn"
                      tabIndex={0}
                    >
                      Add payment method
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div>
          <Button
            text="Disable Text2Give"
            theme="destroy"
            size="md"
            disabled={isSaving}
            onClick={() => setShowDialog(true)}
          />
        </div>
      </div>

      {showDialog && (
        <DeleteConfirmationDialog
          label="Disable Text2Give?"
          description="You will no longer be able to give by sending a text message. This cannot be undone."
          deleteButtonText="Disable Text2Give"
          onCancel={() => setShowDialog(false)}
          onDelete={() => destroyText2GivePhoneNumber()}
        />
      )}
    </>
  )
}
